import React, { useCallback, useRef, useState } from "react";
import { WizardData } from "./OfferWizard";
import ValidatedInput from "./ValidatedInput";
import ValidatedTextarea from "./ValidatedTextarea";
import GuestCounter from "./GuestCounter";
import DateRangeSelector from "./DateRangeSelector";
import ProgressIndicator from "./ProgressIndicator";
import GuestCountPropagationModal from "./GuestCountPropagationModal";
import { useAutoSave } from "../../hooks/useAutoSave";
import {
  updateOffer,
  UpdateOfferPayload,
  previewGuestCountPropagation,
  applyGuestCountPropagation,
  GuestCountPropagationPreview,
} from "../../services/api";
import { Offer } from "../../types";

const GUEST_COUNT_DEBOUNCE_MS = 1000;

interface BasicInfoStepProps {
  data: WizardData;
  updateData: (data: Partial<WizardData>) => void;
  onNext: () => void;
  onCancel: () => void;
  isLoading: boolean;
  onOfferUpdated?: (offer: Offer) => void;
}

const BasicInfoStep: React.FC<BasicInfoStepProps> = ({
  data,
  updateData,
  onNext,
  onCancel,
  isLoading,
  onOfferUpdated,
}) => {
  const [propagationPreview, setPropagationPreview] =
    useState<GuestCountPropagationPreview | null>(null);
  const [showPropagationModal, setShowPropagationModal] = useState(false);
  const [isPropagationLoading, setIsPropagationLoading] = useState(false);
  const [pendingGuestCount, setPendingGuestCount] = useState<number | null>(
    null
  );
  const [propagationError, setPropagationError] = useState<string | null>(null);

  // Debounce timer ref for guest count preview
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // AbortController ref for cancelling in-flight preview requests
  const abortControllerRef = useRef<AbortController | null>(null);

  // Auto-save function (used for normal saves and "Zmień tylko liczbę gości")
  const handleAutoSave = useCallback(
    async (offerData: WizardData) => {
      if (!data.offerId) {
        return;
      }

      const updatePayload: UpdateOfferPayload = {
        title: offerData.title,
        clientName: offerData.clientName,
        clientEmail: offerData.clientEmail,
        clientPhone: offerData.clientPhone,
        description: offerData.description,
        checkInDate: offerData.checkInDate,
        checkOutDate: offerData.checkOutDate,
        numberOfGuests: offerData.numberOfGuests,
        numberOfRooms: Math.ceil(offerData.numberOfGuests / 2),
      };

      await updateOffer(data.offerId, updatePayload);
    },
    [data.offerId]
  );

  const { updateValue: updateAutoSaveData, handleBlur } = useAutoSave(data, {
    delay: 1000,
    onSave: handleAutoSave,
    onError: (error) => {
      console.error("Auto-save error:", error);
    },
  });

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    const newData = { ...data, [name]: value };
    updateData({ [name]: value });
    updateAutoSaveData(newData);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onNext();
  };

  const handleDateChange = (startDate: string, endDate: string) => {
    const newData = {
      ...data,
      checkInDate: startDate,
      checkOutDate: endDate,
    };
    updateData({
      checkInDate: startDate,
      checkOutDate: endDate,
    });
    updateAutoSaveData(newData);
  };

  const handleGuestCountChange = (numberOfGuests: number) => {
    const newData = { ...data, numberOfGuests };
    updateData({ numberOfGuests });

    // Clear pending debounce
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    // Abort any in-flight preview request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    // If no offerId, just auto-save normally
    if (!data.offerId) {
      updateAutoSaveData(newData);
      return;
    }

    // If value hasn't changed, just auto-save
    if (numberOfGuests === data.numberOfGuests) {
      updateAutoSaveData(newData);
      return;
    }

    // Debounce the preview call
    debounceTimerRef.current = setTimeout(async () => {
      const abortController = new AbortController();
      abortControllerRef.current = abortController;

      setIsPropagationLoading(true);
      try {
        const preview = await previewGuestCountPropagation(
          data.offerId!,
          numberOfGuests,
          abortController.signal
        );

        if (abortController.signal.aborted) return;

        if (preview.totalItems > 0) {
          // Show dialog
          setPropagationPreview(preview);
          setPendingGuestCount(numberOfGuests);
          setShowPropagationModal(true);
          // Don't auto-save yet — apply will do it atomically
        } else {
          // No items to propagate — proceed with normal auto-save
          updateAutoSaveData(newData);
        }
      } catch (err: any) {
        if (err?.name === "CanceledError" || err?.name === "AbortError") {
          return;
        }
        console.error("Preview error:", err);
        // Fall back to normal auto-save on error
        updateAutoSaveData(newData);
      } finally {
        if (!abortController.signal.aborted) {
          setIsPropagationLoading(false);
        }
      }
    }, GUEST_COUNT_DEBOUNCE_MS);
  };

  const handlePropagationApply = async () => {
    if (!data.offerId || pendingGuestCount === null) return;

    setIsPropagationLoading(true);
    setPropagationError(null);
    try {
      const updatedOffer = await applyGuestCountPropagation(
        data.offerId,
        pendingGuestCount,
        propagationPreview?.oldCount, // race condition protection
      );
      setShowPropagationModal(false);
      setPropagationPreview(null);
      setPendingGuestCount(null);
      if (onOfferUpdated) {
        onOfferUpdated(updatedOffer);
      }
    } catch (err: any) {
      console.error("Apply error:", err);
      if (err?.response?.status === 409) {
        setPropagationError(
          "Liczba gości została zmieniona w międzyczasie. Odśwież stronę i spróbuj ponownie."
        );
      } else {
        setPropagationError(
          "Nie udało się zaktualizować pozycji. Spróbuj ponownie."
        );
      }
    } finally {
      setIsPropagationLoading(false);
    }
  };

  const handlePropagationCancel = () => {
    setShowPropagationModal(false);
    setPropagationPreview(null);
    setPropagationError(null);

    if (pendingGuestCount !== null) {
      // Trigger normal auto-save with the new numberOfGuests
      const newData = { ...data, numberOfGuests: pendingGuestCount };
      updateAutoSaveData(newData);
      setPendingGuestCount(null);
    }
  };

  const isValid = Boolean(
    data.title &&
      data.clientName &&
      data.clientEmail &&
      data.description &&
      data.checkInDate &&
      data.checkOutDate &&
      data.numberOfGuests > 0
  );

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
      {/* Header */}
      <div className="text-center mb-6 sm:mb-8">
        <h1 className="text-2xl sm:text-3xl font-bold text-neutral-900 mb-2">
          Stwórz nową ofertę
        </h1>
        <p className="text-base sm:text-lg text-neutral-600">
          Wypełnij podstawowe informacje o Twojej ofercie hotelowej
        </p>
      </div>

      <div className="card">
        {/* Header */}
        <div className="px-4 sm:px-8 py-4 sm:py-6 bg-gradient-to-r from-primary-50 to-primary-100 border-b border-neutral-100">
          <h2 className="text-lg sm:text-xl font-semibold text-neutral-900">
            Podstawowe informacje
          </h2>
          <p className="text-sm text-neutral-600 mt-1">
            Wypełnij wymagane pola, aby przejść dalej
          </p>
        </div>

        <form
          onSubmit={handleSubmit}
          className="p-4 sm:p-8 space-y-6 sm:space-y-8"
        >
          {/* Tytuł oferty */}
          <ValidatedInput
            label="Tytuł oferty"
            name="title"
            placeholder="np. Konferencja roczna TechCorp 2025"
            value={data.title}
            onChange={handleChange}
            onBlur={handleBlur}
            required
          />

          {/* Informacje o kliencie */}
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
            <ValidatedInput
              label="Nazwa klienta"
              name="clientName"
              placeholder="Firma lub osoba kontaktowa"
              value={data.clientName}
              onChange={handleChange}
              onBlur={handleBlur}
              required
            />

            <ValidatedInput
              label="Email klienta"
              name="clientEmail"
              type="email"
              placeholder="client@company.com"
              value={data.clientEmail}
              onChange={handleChange}
              onBlur={handleBlur}
              validation={(value) => value.includes("@")}
              required
            />

            <ValidatedInput
              label="Telefon klienta"
              name="clientPhone"
              type="tel"
              placeholder="+48 123 456 789"
              value={data.clientPhone}
              onChange={handleChange}
              onBlur={handleBlur}
            />

            {/* Liczba gości */}
            <div>
              <GuestCounter
                value={data.numberOfGuests || 0}
                onChange={handleGuestCountChange}
              />
              {isPropagationLoading && (
                <span className="text-sm text-neutral-500">Sprawdzam...</span>
              )}
            </div>
          </div>

          {/* Wybór dat */}
          <DateRangeSelector
            checkInDate={data.checkInDate}
            checkOutDate={data.checkOutDate}
            onChange={handleDateChange}
          />

          {/* Jak zrozumieliśmy Twoją sytuację */}
          <div className="space-y-3">
            <ValidatedTextarea
              label="Jak zrozumieliśmy Twoją sytuację"
              name="description"
              placeholder="Opisz sytuację klienta, jego potrzeby i oczekiwania..."
              value={data.description}
              onChange={handleChange}
              onBlur={handleBlur}
              required
              rows={6}
            />

            {/* Pytania pomocnicze */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-left">
              <h4 className="text-sm font-medium text-blue-800 mb-3">
                Pytania, które mogą pomóc w sformułowaniu opisu:
              </h4>
              <ul className="text-sm text-blue-700 space-y-2">
                <li>
                  • Dlaczego zdecydowali się Państwo na zmianę właśnie teraz?
                </li>
                <li>
                  • Jakie alternatywy lub rozwiązania były już rozważane – i co
                  sprawiło, że nie spełniły oczekiwań?
                </li>
                <li>
                  • Co jest dla Państwa najważniejsze w kontekście tej
                  zmiany/projektu? Jakie efekty uznaliby Państwo za sukces?
                </li>
                <li>
                  • Jakie wyzwania lub trudności obecnie najbardziej utrudniają
                  Państwu osiągnięcie tych celów?
                </li>
                <li>
                  • W jaki sposób chcieliby Państwo zweryfikować, że nasza
                  propozycja rzeczywiście rozwiązuje kluczowe problemy i
                  odpowiada na indywidualne potrzeby?
                </li>
              </ul>
            </div>
          </div>

          {/* Progress indicator */}
          <ProgressIndicator
            currentStep={1}
            totalSteps={3}
            isValid={isValid}
            isLoading={isLoading || isPropagationLoading}
            onNext={onNext}
            onCancel={onCancel}
            nextButtonText="Dalej - Plan pobytu"
          />
        </form>
      </div>

      {/* Guest Count Propagation Modal */}
      {propagationPreview && (
        <GuestCountPropagationModal
          isOpen={showPropagationModal}
          onApply={handlePropagationApply}
          onCancel={handlePropagationCancel}
          previewData={propagationPreview}
          isLoading={isPropagationLoading}
          error={propagationError}
        />
      )}
    </div>
  );
};

export default BasicInfoStep;
